// The /albums screen (ADR-0059): the Owner's Albums — named, curated subsets of their
// media, private by default. AlbumsPage is the orchestrator over `useAlbums` (list +
// create + rename + delete); each row opens the album's detail (→ /albums/:id), renames
// inline, or deletes via a two-step confirm — the same CRUD-row idiom as the Look library
// and the device manager. Empty / loading / error states + a status line mirror those
// screens so albums look and announce like the rest of the portal.

import { useState } from "react";
import type { ApiClient, AlbumSummary } from "../api";
import { useAlbums } from "../useAlbums";
import { Loading } from "./Loading";
import { ErrorState } from "./ErrorState";
import { EmptyState } from "./EmptyState";
import { SignedOut } from "./SignedOut";

interface Status {
  kind: "ok" | "error";
  text: string;
}

export interface AlbumsPageProps {
  client: ApiClient;
  /** Open an album's detail view (App routes this to /albums/:id). */
  onOpen: (id: string) => void;
}

export function AlbumsPage({ client, onOpen }: AlbumsPageProps): React.JSX.Element {
  const albums = useAlbums(client);
  const [newName, setNewName] = useState("");
  // A success notice ("Created …"); failures ride `albums.error` (from the hook).
  const [notice, setNotice] = useState<Status | null>(null);

  if (albums.unauthorized) return <SignedOut />;

  // One status line: a load/mutation error wins over a success notice.
  const status: Status | null = albums.error
    ? { kind: "error", text: albums.error }
    : notice;

  async function handleCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const name = newName.trim();
    if (!name || albums.busy) return;
    const id = await albums.create(name);
    if (id) {
      setNewName("");
      setNotice({ kind: "ok", text: `Created “${name}”.` });
    }
  }

  // Gate the success notice on the hook's actual outcome (it swallows errors internally),
  // so a failed rename/delete can never announce success — matching handleCreate's guard.
  async function handleRename(album: AlbumSummary, name: string): Promise<void> {
    if (await albums.rename(album.id, name)) {
      setNotice({ kind: "ok", text: `Renamed to “${name}”.` });
    }
  }

  async function handleDelete(album: AlbumSummary): Promise<void> {
    if (await albums.remove(album.id)) {
      setNotice({ kind: "ok", text: `Deleted “${album.name}”.` });
    }
  }

  return (
    <section className="albums" data-testid="albums-page">
      <div className="albums__head">
        <h2 className="albums__title">Your albums</h2>
        {!albums.loading && (
          <button
            type="button"
            className="btn"
            onClick={albums.reload}
            disabled={albums.busy}
          >
            Refresh
          </button>
        )}
      </div>

      {/* Create: a persistent name + Create control (like the camera/Look CRUD rows). */}
      <form className="albums__create" onSubmit={(e) => void handleCreate(e)}>
        <input
          className="albums__create-input"
          type="text"
          value={newName}
          maxLength={60}
          placeholder="New album name"
          aria-label="New album name"
          data-testid="album-new-name"
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          type="submit"
          className="btn btn--primary"
          data-testid="album-create"
          disabled={!newName.trim() || albums.busy}
        >
          New album
        </button>
      </form>

      {status && (
        <p
          className={`albums__msg albums__msg--${status.kind}`}
          role={status.kind === "error" ? "alert" : "status"}
          data-testid="albums-msg"
        >
          {status.text}
        </p>
      )}

      {albums.loading ? (
        <Loading label="Loading your albums…" />
      ) : albums.error && albums.albums.length === 0 ? (
        <ErrorState
          title="Couldn’t load your albums"
          message={albums.error}
          onRetry={albums.reload}
        />
      ) : albums.albums.length === 0 ? (
        <EmptyState
          icon="▤"
          title="No albums yet"
          message="Create an album, then add Captures and Clips to it from your gallery (Select → Add to album)."
        />
      ) : (
        <ul className="albums__list" data-testid="album-list">
          {albums.albums.map((a) => (
            <AlbumRow
              key={a.id}
              album={a}
              busy={albums.busy}
              onOpen={() => onOpen(a.id)}
              onRename={(name) => handleRename(a, name)}
              onDelete={() => handleDelete(a)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface AlbumRowProps {
  album: AlbumSummary;
  /** True while any mutation is in flight (one at a time). */
  busy: boolean;
  onOpen: () => void;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

/** One album row: Open (the name) · item count · a private/shared badge · Rename · Delete, with
 *  inline rename (60-char cap) + a two-step delete confirm — mirroring the LookLibrary/DeviceManager
 *  idiom (no blocking window.prompt/confirm). */
function AlbumRow({ album, busy, onOpen, onRename, onDelete }: AlbumRowProps): React.JSX.Element {
  const [mode, setMode] = useState<"idle" | "rename" | "delete">("idle");
  const [name, setName] = useState(album.name);

  async function saveRename(): Promise<void> {
    const nm = name.trim();
    if (!nm || nm === album.name) {
      setMode("idle");
      return;
    }
    await onRename(nm);
    setMode("idle");
  }
  async function confirmDelete(): Promise<void> {
    await onDelete();
    setMode("idle"); // the row is usually gone on reload; harmless if not
  }

  if (mode === "rename") {
    return (
      <li className="albums__item">
        <input
          className="albums__rename"
          value={name}
          maxLength={60}
          autoFocus
          aria-label={`New name for ${album.name}`}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void saveRename();
            else if (e.key === "Escape") {
              setName(album.name);
              setMode("idle");
            }
          }}
        />
        <button type="button" onClick={() => void saveRename()} disabled={busy || !name.trim()}>
          Save
        </button>
        <button
          type="button"
          onClick={() => {
            setName(album.name);
            setMode("idle");
          }}
          disabled={busy}
        >
          Cancel
        </button>
      </li>
    );
  }
  if (mode === "delete") {
    return (
      <li className="albums__item">
        <span className="albums__name">{album.name}</span>
        <span className="albums__confirm-q">Delete album? (your media stays)</span>
        <button
          type="button"
          className="albums__del"
          onClick={() => void confirmDelete()}
          disabled={busy}
          aria-label={`Confirm delete ${album.name}`}
        >
          {busy ? "Deleting…" : "Yes, delete"}
        </button>
        <button type="button" onClick={() => setMode("idle")} disabled={busy}>
          Cancel
        </button>
      </li>
    );
  }
  return (
    <li className="albums__item" data-testid="album-row" data-album-id={album.id}>
      <button
        type="button"
        className="albums__name albums__open"
        onClick={onOpen}
        aria-label={`Open ${album.name}`}
      >
        {album.name}
      </button>
      <span className="albums__count" data-testid="album-count">
        {album.itemCount} {album.itemCount === 1 ? "item" : "items"}
      </span>
      <span
        className={`albums__badge albums__badge--${album.visibility}`}
        data-testid="album-visibility"
      >
        {album.visibility === "shared" ? "Shared" : "Private"}
      </span>
      <button type="button" onClick={() => { setName(album.name); setMode("rename"); }} aria-label={`rename ${album.name}`}>
        Rename
      </button>
      <button type="button" onClick={() => setMode("delete")} aria-label={`delete ${album.name}`}>
        Delete
      </button>
    </li>
  );
}
